import { useEffect, useState } from 'react';
import Image from 'next/image';
import { NextSeo } from 'next-seo';
import { AppRoutes } from 'src/lib/routes';
import ReferralIntro from 'src/components/ReferralIntro';
import WithDefaultJsonLd from 'src/components/WithDefaultJsonLd';
import useCurrentUser from 'src/hooks/useCurrentUser';
import { useRouter } from 'next/router';
import Copyable from 'src/components/Copyable';
import CopySvg from 'public/images/svg_icons/copy.svg';
import Link from 'next/link';
import Loading from 'src/components/Loading';
import Illu from 'public/images/illu_6.png';

const metaTitleSharing = 'Refer a Friend and Earn!';
const metaTitleExploring = 'Welcome to Seek Sophie!'
const metaDescSharing = 'Invite your friends to try Seek Sophie for the 1st time. They get $10 off their first booking, and you earn $10 in credits when they complete their experience!';
const metaDescExploring = 'Apply the discount code below at checkout to get $10 off your first experience'

const Invite = () => {
  const [currentUser, , , , isLoading] = useCurrentUser();
  const router = useRouter();
  const { code } = router.query;
  const [isSharing, setIsSharing] = useState<boolean>(true);

  useEffect(() => {
    if (currentUser && (code === currentUser.referralCode) || !code) {
      setIsSharing(true)
    } else {
      setIsSharing(false)
    }
  }, [code, currentUser?.referralCode])

  return (
    <>
      <NextSeo
        title={isSharing ? metaTitleSharing : metaTitleExploring}
        description={isSharing ? metaDescSharing : metaDescExploring}
        canonical={AppRoutes.inviteRoute().toUrl()}
        openGraph={{
          title: isSharing ? metaTitleSharing : metaTitleExploring,
          description: isSharing ? metaDescSharing : metaDescExploring,
          url: AppRoutes.inviteRoute().toUrl(),
          images: [
            {
              url: Illu.src,
              width: 1200,
              height: 800,
            },
          ],
        }}
      />
      <WithDefaultJsonLd />

      <div className="tw-bg-brand-yellow tw-py-16 md:tw-py-24">
        <div className="container tw-max-w-2xl">
          <div className="tw-relative tw-text-center">
            <Image
              src={Illu}
              alt="Seek Sophie referral"
              fill
              className="tw-object-cover tw-w-auto tw-h-40 md:tw-h-72 tw-static tw-inline-block tw-mb-6 md:tw-mb-8"
            />
          </div>

          {isLoading ? (
            <Loading className="tw-justify-center tw-pt-20" />
          ) : isSharing ? (
            <>
              <h1 className="tw-text-center tw-mb-3 tw-font-styleSans tw-font-semibold tw-text-2xl md:tw-text-4xl tw-leading-none md:tw-leading-none">{metaTitleSharing}</h1>
              <div className="tw-mb-3 tw-text-center">{metaDescSharing}</div>
              <div>
                <ReferralIntro
                  referralCode={currentUser?.referralCode}
                />
              </div>
            </>
          ) : code ? (
            <>
              <h1 className="tw-text-center tw-mb-3 tw-font-styleSans tw-font-semibold tw-text-2xl md:tw-text-4xl tw-leading-none md:tw-leading-none">{metaTitleExploring}</h1>
              <div className="tw-mb-3 tw-text-center">{metaDescExploring}</div>
              <div>
                <div className="md:tw-px-10">
                  <div className="tw-bg-white tw-border tw-border-grey-lighter tw-rounded-4xl tw-px-3 tw-py-2.5 tw-flex tw-items-center tw-justify-between tw-mb-3">
                    <input
                      readOnly
                      value={code}
                      type="text"
                      className="tw-appearance-none tw-outline-none tw-flex-grow tw-text-brand-navy tw-placeholder-brand-navy"
                    />
                    <Copyable as="div" className="tw-cursor-pointer" copyContent={String(code)}>
                      <CopySvg width={24} height={24} className="tw-text-orange tw-ml-2" />
                    </Copyable>
                  </div>
                </div>

                <div className="tw-text-center">
                  <Link legacyBehavior prefetch={false} href={AppRoutes.rootRoute().toPath()}>
                    <a className="btn btn-primary">Start Exploring</a>
                  </Link>
                </div>
              </div>
            </>
          ) : ''}
        </div>
      </div>
    </>
  );
};

export default Invite;
